import { useState } from 'react'
import { Input } from '@/components/ui/input'
import type { OptionDef } from '../query/types'

/** Above this many options the list gets a search field. */
const SEARCH_ABOVE = 8

/**
 * The choices of a list filter as native checkboxes (design §5.2). `options` is null while they
 * load. The order of `value` is the order they were ticked in.
 */
export function OptionChecklist({
  label,
  options,
  value,
  onChange,
  displayLabel,
}: {
  /** The name of the group, e.g. "Eigenaar". */
  label: string
  options: readonly OptionDef[] | null
  value: readonly string[]
  onChange: (next: string[]) => void
  /** Another text than the option's own, e.g. "Geen eigenaar" for "Niemand". */
  displayLabel?: (option: OptionDef) => string
}) {
  const [term, setTerm] = useState('')
  const text = (option: OptionDef): string => displayLabel?.(option) ?? option.label
  const needle = term.trim().toLowerCase()
  const shown = options?.filter((option) => text(option).toLowerCase().includes(needle)) ?? []

  function toggle(id: string, on: boolean) {
    onChange(on ? [...value, id] : value.filter((chosen) => chosen !== id))
  }

  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1">
      {options && options.length > SEARCH_ABOVE ? (
        <Input
          type="search"
          aria-label={`Zoek in ${label}`}
          placeholder="Zoeken…"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          className="max-md:h-11 max-md:text-base"
        />
      ) : null}
      <div className="flex max-h-64 flex-col overflow-y-auto">
        {options === null ? (
          <p role="status" className="px-2 py-3 text-sm text-muted-foreground">
            Laden…
          </p>
        ) : options.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Geen opties</p>
        ) : shown.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Geen resultaten</p>
        ) : (
          shown.map((option) => (
            <label
              key={option.value}
              className="flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted max-md:min-h-11"
            >
              <input
                type="checkbox"
                className="size-4 shrink-0 accent-primary"
                checked={value.includes(option.value)}
                onChange={(event) => toggle(option.value, event.target.checked)}
              />
              <span className="min-w-0 break-words">{text(option)}</span>
            </label>
          ))
        )}
      </div>
    </div>
  )
}
